// Stimulus controller manifest — hand-curated.
// New controllers must be both imported AND registered (`application.register(...)`).
// Group registrations near related controllers; don't rely on alphabetical order.
//
// A heavy controller used by one surface only (Gantt, CR editor, agent console, portals…) is
// declared with `registerLazy('id', () => import('./x_controller'))` instead: it leaves the
// initial bundle and loads on the first matching `data-controller`. Read the header of
// `utils/lazy_controller_registry.js` before deferring one: some controllers must stay eager.

import { application } from './application';
import { lazyControllerRegistry } from '../utils/lazy_controller_registry';

const registerLazy = lazyControllerRegistry(application);

import AddressAutocompleteController from './address_autocomplete_controller';
application.register('address-autocomplete', AddressAutocompleteController);

import AdvanceWarningController from './advance_warning_controller';
application.register('advance-warning', AdvanceWarningController);

import AgentConsoleController from './agent_console_controller';
application.register('agent-console', AgentConsoleController);

import AmountValidatorController from './amount_validator_controller';
import SubcontractCapController from './subcontract_cap_controller';
import SubcontractAgreedAmountController from './subcontract_agreed_amount_controller';
application.register('amount-validator', AmountValidatorController);
application.register('subcontract-cap', SubcontractCapController);
application.register('subcontract-agreed-amount', SubcontractAgreedAmountController);

import AnnotationModeController from './annotation_mode_controller';
application.register('annotation-mode', AnnotationModeController);

import AnnotationPanelController from './annotation_panel_controller';
application.register('annotation-panel', AnnotationPanelController);

import AnnotationViewerController from './annotation_viewer_controller';
application.register('annotation-viewer', AnnotationViewerController);

registerLazy('apex-chart', () => import('./apex_chart_controller'));

import AttachmentsFieldController from './attachments_field_controller';
application.register('attachments-field', AttachmentsFieldController);

registerLazy('audio-player', () => import('./audio_player_controller'));

import AgentReviewAttachController from './agent_review_attach_controller';
application.register('agent-review-attach', AgentReviewAttachController);

registerLazy(
  'agent-review-section-reload',
  () => import('./agent_review_section_reload_controller')
);

registerLazy('agent-review-selection', () => import('./agent_review_selection_controller'));

registerLazy('gantt', () => import('./gantt_controller'));

import AssignmentListController from './assignment_list_controller';
application.register('assignment-list', AssignmentListController);

import AutoOpenModalController from './auto_open_modal_controller';
application.register('auto-open-modal', AutoOpenModalController);

import AutofocusController from './autofocus_controller';
application.register('autofocus', AutofocusController);

import ButtonLoadingController from './button_loading_controller';
application.register('button-loading', ButtonLoadingController);

import AutoSubmitController from './auto_submit_controller';
application.register('auto-submit', AutoSubmitController);

import FrameHistorySyncController from './frame_history_sync_controller';
application.register('frame-history-sync', FrameHistorySyncController);

import CasquetteTagsController from './casquette_tags_controller';
application.register('casquette-tags', CasquetteTagsController);

import CheckboxGroupController from './checkbox_group_controller';
application.register('checkbox-group', CheckboxGroupController);

import ClientContactController from './client_contact_controller';
application.register('client-contact', ClientContactController);

import ClientFormController from './client_form_controller';
application.register('client-form', ClientFormController);

import ClickableRowController from './clickable_row_controller';
application.register('clickable-row', ClickableRowController);

import ClipboardController from './clipboard_controller';
application.register('clipboard', ClipboardController);

import CommandPaletteController from './command_palette_controller';
application.register('command-palette', CommandPaletteController);

import ConfirmationModalController from './confirmation_modal_controller';
application.register('confirmation-modal', ConfirmationModalController);

import ConfirmationModalTriggerController from './confirmation_modal_trigger_controller';
application.register('confirmation-modal-trigger', ConfirmationModalTriggerController);

import ConfirmationInputController from './confirmation_input_controller';
application.register('confirmation-input', ConfirmationInputController);

import ContractSelectorController from './contract_selector_controller';
application.register('contract-selector', ContractSelectorController);

import ContractorComboboxController from './contractor_combobox_controller';
application.register('contractor-combobox', ContractorComboboxController);

import SearchableSelectController from './searchable_select_controller';
application.register('searchable-select', SearchableSelectController);

import AutocompleteInputController from './autocomplete_input_controller';
application.register('autocomplete-input', AutocompleteInputController);

registerLazy('contractor-link-search', () => import('./contractor_link_search_controller'));

import ContractorTradesController from './contractor_trades_controller';
application.register('contractor-trades', ContractorTradesController);

import CopyPreviousCommentController from './copy_previous_comment_controller';
application.register('copy-previous-comment', CopyPreviousCommentController);

import CoverPhotoPickerController from './cover_photo_picker_controller';
application.register('cover-photo-picker', CoverPhotoPickerController);

import CrAnchorScrollController from './cr_anchor_scroll_controller';
application.register('cr-anchor-scroll', CrAnchorScrollController);

import CrDiffusionGuardController from './cr_diffusion_guard_controller';
application.register('cr-diffusion-guard', CrDiffusionGuardController);

import CrInlineFieldController from './cr_inline_field_controller';
application.register('cr-inline-field', CrInlineFieldController);

registerLazy('cr-save-indicator', () => import('./cr_save_indicator_controller'));

import CrStructurePreviewController from './cr_structure_preview_controller';
application.register('cr-structure-preview', CrStructurePreviewController);

import PhotoPickerController from './photo_picker_controller';
application.register('photo-picker', PhotoPickerController);

import PlanPinController from './plan_pin_controller';
application.register('plan-pin', PlanPinController);

import PlanPinPopoverController from './plan_pin_popover_controller';
application.register('plan-pin-popover', PlanPinPopoverController);

import PlanRasterController from './plan_raster_controller';
application.register('plan-raster', PlanRasterController);

// #657 (T13) — la visionneuse plan mobile est rendue par `_document_section`, qui
// sert les QUATRE surfaces sans discriminant de rôle : elle est donc enregistrée
// ici ET dans le bundle public (`public_share.js`). En lecture seule de bout en
// bout, elle ne dénature pas l'allowlist publique.
import PlanViewerController from './plan_viewer_controller';
application.register('plan-viewer', PlanViewerController);

import TagRemoveController from './tag_remove_controller';
application.register('tag-remove', TagRemoveController);

import TagComboboxController from './tag_combobox_controller';
application.register('tag-combobox', TagComboboxController);

registerLazy('effectifs-filter', () => import('./effectifs_filter_controller'));

import FileDropzoneController from './file_dropzone_controller';
application.register('file-dropzone', FileDropzoneController);

import DeleteStatementModalController from './delete_statement_modal_controller';
application.register('delete-statement-modal', DeleteStatementModalController);

import DeleteTsModalController from './delete_ts_modal_controller';
application.register('delete-ts-modal', DeleteTsModalController);

// Connexion rapide dev (plan 2026-10-18) : la pastille « DEV » de `dev/logins/_pill`, développement
// seulement. Différé : jamais rendu en production, il n'a rien à faire dans le bundle initial.
registerLazy('dev-login', () => import('./dev_login_controller'));

registerLazy('devis-compare', () => import('./devis_compare_controller'));

import DatePickerController from './date_picker_controller';
application.register('date-picker', DatePickerController);

import NextMeetingSuggestionsController from './next_meeting_suggestions_controller';
application.register('next-meeting-suggestions', NextMeetingSuggestionsController);

import DgdAllocationController from './dgd_allocation_controller';
application.register('dgd-allocation', DgdAllocationController);

import DgdRetentionModalController from './dgd_retention_modal_controller';
application.register('dgd-retention-modal', DgdRetentionModalController);

import DirectDocumentUploadController from './direct_document_upload_controller';
application.register('direct-document-upload', DirectDocumentUploadController);

import DisclosureController from './disclosure_controller';
application.register('disclosure', DisclosureController);

import DisclosurePanelController from './disclosure_panel_controller';
application.register('disclosure-panel', DisclosurePanelController);

import ColorPickerController from './color_picker_controller';
application.register('color-picker', ColorPickerController);

import DisclosureGroupController from './disclosure_group_controller';
application.register('disclosure-group', DisclosureGroupController);

// Refonte de l'espace chantier V2, P6.8 : « Informations du projet » de Pilotage, repliée au
// téléphone dans un `<details>` rouvert d'office à partir de 768 px (`projects/_project_info`).
// Aussi enregistré par `public_share.js` (letterhead de la page publique).
import BreakpointOpenController from './breakpoint_open_controller';
application.register('breakpoint-open', BreakpointOpenController);

import SectionCollapseController from './section_collapse_controller';
application.register('section-collapse', SectionCollapseController);

import DocumentUploadController from './document_upload_controller';
application.register('document-upload', DocumentUploadController);

import DropdownController from './dropdown_controller';
application.register('dropdown', DropdownController);

import DropzoneController from './dropzone_controller';
application.register('dropzone', DropzoneController);

import EmailController from './email_controller';
application.register('email', EmailController);

import FileTypeValidatorController from './file_type_validator_controller';
application.register('file-type-validator', FileTypeValidatorController);

import ImageUploadFieldController from './image_upload_field_controller';
application.register('image-upload-field', ImageUploadFieldController);

import FilterPillsController from './filter_pills_controller';
application.register('filter-pills', FilterPillsController);

// D'emblée : il porte le choix du fichier, geste principal des pages de dépôt (portail entreprise,
// espace entreprise). Différé, un fichier choisi avant son connect() était ignoré.
import FileUploadController from './file_upload_controller';
application.register('file-upload', FileUploadController);

import FlashController from './flash_controller';
application.register('flash', FlashController);

import ForgotPasswordLinkController from './forgot_password_link_controller';
application.register('forgot-password-link', ForgotPasswordLinkController);

import HelloController from './hello_controller';
application.register('hello', HelloController);

import InlineLabelController from './inline_label_controller';
application.register('inline-label', InlineLabelController);

import InvoiceLineUploaderController from './invoice_line_uploader_controller';
application.register('invoice-line-uploader', InvoiceLineUploaderController);

import InvoiceLinesTableController from './invoice_lines_table_controller';
application.register('invoice-lines-table', InvoiceLinesTableController);

import JericoInsightController from './jerico_insight_controller';
application.register('jerico-insight', JericoInsightController);

import LazyIframeController from './lazy_iframe_controller';
application.register('lazy-iframe', LazyIframeController);

import LoadingModalController from './loading_modal_controller';
application.register('loading-modal', LoadingModalController);

import LocalStorageDismissController from './local_storage_dismiss_controller';
application.register('local-storage-dismiss', LocalStorageDismissController);

import MagicLinkAutoSubmitController from './magic_link_auto_submit_controller';
application.register('magic-link-auto-submit', MagicLinkAutoSubmitController);

registerLazy('mermaid-diagram', () => import('./mermaid_diagram_controller'));

import InlineStatementController from './inline_statement_controller';
application.register('inline-statement', InlineStatementController);

import InlineStatementErrorController from './inline_statement_error_controller';
application.register('inline-statement-error', InlineStatementErrorController);

import InsuranceYearController from './insurance_year_controller';
application.register('insurance-year', InsuranceYearController);

import ModalController from './modal_controller';
application.register('modal', ModalController);

import ModalOpenerController from './modal_opener_controller';
application.register('modal-opener', ModalOpenerController);

import MoeFeeAssietteController from './moe_fee_assiette_controller';
application.register('moe-fee-assiette', MoeFeeAssietteController);

import MoeFeeGlobalVatController from './moe_fee_global_vat_controller';
application.register('moe-fee-global-vat', MoeFeeGlobalVatController);

import MoeFeeInvoiceEditController from './moe_fee_invoice_edit_controller';
application.register('moe-fee-invoice-edit', MoeFeeInvoiceEditController);

import MoeFeeTemplatePickerController from './moe_fee_template_picker_controller';
application.register('moe-fee-template-picker', MoeFeeTemplatePickerController);

import MoeFeeLineBuilderController from './moe_fee_line_builder_controller';
application.register('moe-fee-line-builder', MoeFeeLineBuilderController);

import MultiSelectController from './multi_select_controller';
application.register('multi-select', MultiSelectController);

import CustomLotController from './custom_lot_controller';
application.register('custom-lot', CustomLotController);

import MultiTokenCheckboxController from './multi_token_checkbox_controller';
application.register('multi-token-checkbox', MultiTokenCheckboxController);

import MultiStepModalController from './multi_step_modal_controller';
application.register('multi-step-modal', MultiStepModalController);

import OnboardingAddController from './onboarding_add_controller';
application.register('onboarding-add', OnboardingAddController);

import OnboardingEditController from './onboarding_edit_controller';
application.register('onboarding-edit', OnboardingEditController);

import OsSetupSkipController from './os_setup_skip_controller';
application.register('os-setup-skip', OsSetupSkipController);

import OrgSwitcherController from './org_switcher_controller';
application.register('org-switcher', OrgSwitcherController);

import PairingCountdownController from './pairing_countdown_controller';
application.register('pairing-countdown', PairingCountdownController);

registerLazy('password-strength', () => import('./password_strength_controller'));

import PasswordVisibilityController from './password_visibility_controller';
application.register('password-visibility', PasswordVisibilityController);

import PayloadCopyController from './payload_copy_controller';
application.register('payload-copy', PayloadCopyController);

import PayloadEditController from './payload_edit_controller';
application.register('payload-edit', PayloadEditController);

import PdfPreviewController from './pdf_preview_controller';
application.register('pdf-preview', PdfPreviewController);

import DiscussionAutoscrollController from './discussion_autoscroll_controller';
application.register('discussion-autoscroll', DiscussionAutoscrollController);

import PhasePanelController from './phase_panel_controller';
application.register('phase-panel', PhasePanelController);

import PlanningDependencyPickerController from './planning_dependency_picker_controller';
application.register('planning-dependency-picker', PlanningDependencyPickerController);

import PlanningPhaseFormController from './planning_phase_form_controller';
application.register('planning-phase-form', PlanningPhaseFormController);

// Chargé d'emblée : il écrit (enregistrement automatique du calendrier de l'agence).
import AgencyCalendarController from './agency_calendar_controller';
application.register('agency-calendar', AgencyCalendarController);

// Chargé d'emblée : il débloque « Enregistrer » du tiroir « Réglages » du planning et tient ses brouillons.
import PlanSettingsController from './plan_settings_controller';
application.register('plan-settings', PlanSettingsController);

import PlanningImpactRevalidationController from './planning_impact_revalidation_controller';
application.register('planning-impact-revalidation', PlanningImpactRevalidationController);

registerLazy('planning-fullscreen', () => import('./planning_fullscreen_controller'));

import PlanningFrameSessionController from './planning_frame_session_controller';
application.register('planning-frame-session', PlanningFrameSessionController);

registerLazy('reschedule-card', () => import('./reschedule_card_controller'));

import RescheduleDatesController from './reschedule_dates_controller';
application.register('reschedule-dates', RescheduleDatesController);

import ImagePreviewController from './image_preview_controller';
application.register('image-preview', ImagePreviewController);

import ImageSkeletonController from './image_skeleton_controller';
application.register('image-skeleton', ImageSkeletonController);

registerLazy('export-actor-filter', () => import('./export_actor_filter_controller'));

import ExportScalePeriodController from './export_scale_period_controller';
application.register('export-scale-period', ExportScalePeriodController);

import ExportTriggerController from './export_trigger_controller';
application.register('export-trigger', ExportTriggerController);

import ExportModalController from './export_modal_controller';
application.register('export-modal', ExportModalController);

import AutoDownloadController from './auto_download_controller';
application.register('auto-download', AutoDownloadController);

import PilotDurationController from './pilot_duration_controller';
application.register('pilot-duration', PilotDurationController);

import PilotModalController from './pilot_modal_controller';
application.register('pilot-modal', PilotModalController);

import PilotToggleController from './pilot_toggle_controller';
application.register('pilot-toggle', PilotToggleController);

import PollingRefreshController from './polling_refresh_controller';
application.register('polling-refresh', PollingRefreshController);

import ProductUpdatesFilterController from './product_updates_filter_controller';
application.register('product-updates-filter', ProductUpdatesFilterController);

import ProgressCalculatorController from './progress_calculator_controller';
application.register('progress-calculator', ProgressCalculatorController);

import PriceRevisionFormController from './price_revision_form_controller';
application.register('price-revision-form', PriceRevisionFormController);

import ProjectSelectionController from './project_selection_controller';
application.register('project-selection', ProjectSelectionController);

import ProrataContributorsPopoverController from './prorata_contributors_popover_controller';
application.register('prorata-contributors-popover', ProrataContributorsPopoverController);

import ProrataExpensesPreviewController from './prorata_expenses_preview_controller';
application.register('prorata-expenses-preview', ProrataExpensesPreviewController);

import ProrataWizardController from './prorata_wizard_controller';
application.register('prorata-wizard', ProrataWizardController);

import RadioCardController from './radio_card_controller';
application.register('radio-card', RadioCardController);

registerLazy('revision-preview', () => import('./revision_preview_controller'));

import RangeInputController from './range_input_controller';
application.register('range-input', RangeInputController);

import RegularizationModeController from './regularization_mode_controller';
application.register('regularization-mode', RegularizationModeController);

import RegularizationPickerController from './regularization_picker_controller';
application.register('regularization-picker', RegularizationPickerController);

import RegularizationTemplateBuilderController from './regularization_template_builder_controller';
application.register('regularization-template-builder', RegularizationTemplateBuilderController);

import RemarkAutoOpenController from './remark_auto_open_controller';
application.register('remark-auto-open', RemarkAutoOpenController);

import RemarkLotController from './remark_lot_controller';
application.register('remark-lot', RemarkLotController);

import RemarkStatusBuilderController from './remark_status_builder_controller';
application.register('remark-status-builder', RemarkStatusBuilderController);

registerLazy('remarks-filter', () => import('./remarks_filter_controller'));

// D'emblée : il reçoit le clic « Afficher » des soldées masquées (PR 2 c-cr-lisible, R10).
// Différé, un clic avant sa connexion serait perdu sans un mot.
import RemarksSolvedRevealController from './remarks_solved_reveal_controller';
application.register('remarks-solved-reveal', RemarksSolvedRevealController);

import RemarkDueShiftController from './remark_due_shift_controller';
application.register('remark-due-shift', RemarkDueShiftController);

registerLazy('remark-swipe', () => import('./remark_swipe_controller'));

// D'emblée : seul « Annuler » d'une remarque soldée d'un glisser. Différé, un glisser validé
// avant sa connexion n'aurait pas de toast d'annulation.
import RemarkUndoToastController from './remark_undo_toast_controller';
application.register('remark-undo-toast', RemarkUndoToastController);

import RemoveInvoiceChoiceController from './remove_invoice_choice_controller';
application.register('remove-invoice-choice', RemoveInvoiceChoiceController);

import ReplyComposerController from './reply_composer_controller';
application.register('reply-composer', ReplyComposerController);

import ReviewConfirmController from './review_confirm_controller';
application.register('review-confirm', ReviewConfirmController);

import ReviewModeController from './review_mode_controller';
application.register('review-mode', ReviewModeController);

import ReviewPadController from './review_pad_controller';
application.register('review-pad', ReviewPadController);

import RoleChangeController from './role_change_controller';
application.register('role-change', RoleChangeController);

// D'emblée : la modale « Diffusion du CR » écoute `turbo:before-stream-render` pour garder filtre,
// repli des collèges et focus à travers le chokepoint ; différé, une révocation faite avant sa
// connexion rendrait le focus à <body>.
import ShareReceiptController from './share_receipt_controller';
application.register('share-receipt', ShareReceiptController);

import ScrollspyController from './scrollspy_controller';
application.register('scrollspy', ScrollspyController);

import ScrollIntoViewController from './scroll_into_view_controller';
application.register('scroll-into-view', ScrollIntoViewController);

import SidebarController from './sidebar_controller';
application.register('sidebar', SidebarController);

import SidebarCollapseController from './sidebar_collapse_controller';
application.register('sidebar-collapse', SidebarCollapseController);

import SiretController from './siret_controller';
application.register('siret', SiretController);

import SireneLookupController from './sirene_lookup_controller';
application.register('sirene-lookup', SireneLookupController);

import SireneTypeaheadController from './sirene_typeahead_controller';
application.register('sirene-typeahead', SireneTypeaheadController);

import SlideOverController from './slide_over_controller';
application.register('slide-over', SlideOverController);

import SlideOverTriggerController from './slide_over_trigger_controller';
application.register('slide-over-trigger', SlideOverTriggerController);

registerLazy('sortable', () => import('./sortable_controller'));

import StickyRevealController from './sticky_reveal_controller';
application.register('sticky-reveal', StickyRevealController);

import TabsController from './tabs_controller';
application.register('tabs', TabsController);

import StarToggleController from './star_toggle_controller';
application.register('star-toggle', StarToggleController);

import ToggleController from './toggle_controller';
application.register('toggle', ToggleController);

import VisaTrackerController from './visa_tracker_controller';
application.register('visa-tracker', VisaTrackerController);

import ValidationTrackerToggleController from './validation_tracker_toggle_controller';
application.register('validation-tracker-toggle', ValidationTrackerToggleController);

import TooltipController from './tooltip_controller';
application.register('tooltip', TooltipController);

import TradesConfigController from './trades_config_controller';
application.register('trades-config', TradesConfigController);

import TradeValidatorController from './trade_validator_controller';
application.register('trade-validator', TradeValidatorController);

// D'emblée : son action est le premier clic de la page de dépôt (« Déposer une facture » / devis).
// Différé (#875), ce clic arrivait avant son connect() et la zone de dépôt restait grisée (smoke staging).
import UploadTypeSelectorController from './upload_type_selector_controller';
application.register('upload-type-selector', UploadTypeSelectorController);

import VatCalculatorController from './vat_calculator_controller';
application.register('vat-calculator', VatCalculatorController);

import VatToggleController from './vat_toggle_controller';
application.register('vat-toggle', VatToggleController);

import WhatsNewController from './whats_new_controller';
application.register('whats-new', WhatsNewController);

import WpsFormController from './wps_form_controller';
application.register('wps-form', WpsFormController);
